import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "types";
import { API_BASE_URL } from "utils/constants";

export const baseUrl = API_BASE_URL;

/** Same-origin proxy path for credentialed bridge calls (keep in sync with setupProxy.js and server.js). */
export const AUTH_BRIDGE_DEV_PROXY_PREFIX = "/__vle-auth-bridge__";

function tryParseApiOrigin(apiBase: string): string | null {
  try {
    const trimmed = apiBase.trim();
    const withScheme = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    return new URL(withScheme).origin;
  } catch {
    return null;
  }
}

/** Same-origin auth bridge via `setupProxy.js` is only for CRA on this machine. Deployed dev/staging/prod hit the API URL directly. */
function isLocalCraHost(): boolean {
  if (typeof window === "undefined" || !window.location?.hostname) {
    return false;
  }
  const h = window.location.hostname.toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]";
}

/**
 * URL for credentialed `fetch(..., { credentials: "include" })` bridge calls.
 * On **localhost** only, when SPA origin ≠ API origin, uses same-origin `…/__vle-auth-bridge__/…` (CRA `setupProxy.js`).
 * On deployed dev/production hosts, always uses `REACT_APP_API_URL` directly — configure API CORS for credentialed cross-origin if needed.
 * To disable the local proxy too: `REACT_APP_AUTH_BRIDGE_PROXY=0` or `REACT_APP_DEV_AUTH_BRIDGE_PROXY=0`.
 */
export function resolveAuthBridgeCredentialedUrl(apiPath: string): string {
  const path = apiPath.startsWith("/") ? apiPath : `/${apiPath}`;
  const direct = `${baseUrl}${path}`;
  if (
    process.env.REACT_APP_DEV_AUTH_BRIDGE_PROXY === "0" ||
    process.env.REACT_APP_AUTH_BRIDGE_PROXY === "0"
  ) {
    return direct;
  }
  if (typeof window === "undefined" || !window.location?.origin) {
    return direct;
  }
  if (!isLocalCraHost()) {
    return direct;
  }
  const apiOrigin = tryParseApiOrigin(baseUrl);
  if (!apiOrigin || window.location.origin === apiOrigin) {
    return direct;
  }
  return `${window.location.origin}${AUTH_BRIDGE_DEV_PROXY_PREFIX}${path}`;
}

/** Google reCAPTCHA v3 site key (public). Set `REACT_APP_RECAPTCHA_SITE_KEY` in `.env`. */
export const recaptchaSiteKey =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY?.trim() ?? "";

export const gpayMerchantId =
  process.env.REACT_APP_GPAY_MERCHANT_ID?.trim() ||
  process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID?.trim() ||
  "";

export const gpayMerchantName =
  process.env.REACT_APP_GPAY_MERCHANT_NAME?.trim() || "VLEMobile.com";

export const applePayMerchantId =
  process.env.REACT_APP_APPLE_PAY_MERCHANT_ID?.trim() ||
  process.env.REACT_APP_WALLET_MERCHANT_ID?.trim() ||
  "merchant.com.vlemobile";

export const applePayMerchantName =
  process.env.REACT_APP_APPLE_PAY_MERCHANT_NAME?.trim() || "VLEMobile.com";

const defaultHeaders = {
  Accept: "application/json",
};

const prepareHeaders = (headers: any, { getState }) => {
  const token = Array.isArray((getState() as RootState)?.global?.user?.tokens)
    ? (getState() as RootState)?.global?.user?.tokens?.[0]?.token
    : (getState() as RootState)?.global?.user?.tokens;

  if (token) {
    headers.set("api-token", token);
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
};
export const baseQuery = fetchBaseQuery({
  baseUrl,
  headers: defaultHeaders,
  prepareHeaders,
});

export const formatErrors = (errors: any) => {
  console.log("formatErrors", errors?.data?.messages?.[0]);
  if (
    errors &&
    ((typeof errors.emailError === "string" && errors.emailError.trim()) ||
      (typeof errors.mobileError === "string" && errors.mobileError.trim()))
  ) {
    return errors;
  }
  if (
    errors?.data &&
    ((typeof errors.data.emailError === "string" &&
      errors.data.emailError.trim()) ||
      (typeof errors.data.mobileError === "string" &&
        errors.data.mobileError.trim()))
  ) {
    return errors.data;
  }
  /** Plain `{ message }` body (e.g. OTP send) — show in toast, not as field errors. */
  if (
    errors &&
    typeof errors.message === "string" &&
    errors.message.trim() &&
    !errors.emailError &&
    !errors.mobileError
  ) {
    return { message: errors.message };
  }
  if (
    errors?.data &&
    typeof errors.data.message === "string" &&
    errors.data.message.trim() &&
    !errors.data.emailError &&
    !errors.data.mobileError
  ) {
    return { message: errors.data.message };
  }
  if (
    errors?.data?.messages &&
    Array.isArray(errors.data.messages) &&
    errors.data.messages.length > 0
  ) {
    return errors.data.messages
      .map((m: unknown) => String(m || "").trim())
      .filter(Boolean)
      .join(". ");
  } else if (
    errors?.messages &&
    Array.isArray(errors.messages) &&
    errors.messages.length > 0
  ) {
    return errors.messages
      .map((m: unknown) => String(m || "").trim())
      .filter(Boolean)
      .join(". ");
  } else if (typeof errors?.messages === "string") {
    return errors.messages;
  } else {
    return "Something went wrong";
  }
};

export const transformPaginatedResponse = (data) => data?.response;

export const HTTP_METHODS = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  DELETE: "DELETE",
  PATCH: "PATCH",
};

export const endpoints = {
  baseUrl,
  verifyDev: {
    url: "/auth/user/verify-dev",
    method: HTTP_METHODS.POST,
  },
  // General & Auth
  login: {
    url: "/auth/user/login",
    method: HTTP_METHODS.POST,
  },
  verifyOTP: {
    url: "/auth/user/verify-two-factor-auth",
    method: HTTP_METHODS.POST,
  },
  forgotPassword: {
    url: "/auth/user/forgot-password",
    method: HTTP_METHODS.POST,
  },
  resetPassword: {
    url: "/auth/user/change-password",
    method: HTTP_METHODS.POST,
  },
  verifyResetPasswordOtp: {
    url: "/auth/user/reset-password-otp",
    method: HTTP_METHODS.POST,
  },
  sendCodeToPhone: {
    url: "/auth/user/send-code",
    method: HTTP_METHODS.POST,
  },
  authExchange: {
    url: "/auth/exchange",
    method: HTTP_METHODS.POST,
  },
  getProfile: {
    url: "/api/my-account/get-profile",
    method: HTTP_METHODS.GET,
  },
  customerReview: {
    url: "/api/my-account/customer-review",
    method: HTTP_METHODS.POST,
  },
  updateProfile: {
    url: "/api/my-account/update-profile",
    method: HTTP_METHODS.POST,
  },
  dashboard: {
    url: "/api/my-account/dashboard",
    method: HTTP_METHODS.GET,
  },
  simService: {
    url: "/api/my-account/sim-service",
    method: HTTP_METHODS.GET,
  },
  sendOtpInCheckout: {
    url: "/api/send-otp",
    method: HTTP_METHODS.POST,
  },
  verifyOtpInCheckout: {
    url: "/api/verify-otp",
    method: HTTP_METHODS.POST,
  },
  resendOtpInCheckout: {
    url: "/api/resend-otp",
    method: HTTP_METHODS.POST,
  },
  editLineName: {
    url: "/api/my-account/edit-line",
    method: HTTP_METHODS.PUT,
  },
  countriesList: {
    url: "/api/my-account/list-of-countries",
    method: HTTP_METHODS.GET,
  },
  listOfRoamingCountries: {
    url: "/api/my-account/list-of-roamingCountries",
    method: HTTP_METHODS.GET,
  },
  checkAvailability: {
    url: "/api/check-availability",
    method: HTTP_METHODS.POST,
  },
  deviceCompatibility: {
    url: "/api/deviceCompatibility",
    method: HTTP_METHODS.POST,
  },
  getDevices: {
    url: "/api/deviceCompatibility",
    method: HTTP_METHODS.GET,
  },
  validateDevice: {
    url: "/api/payment/validate-device",
    method: HTTP_METHODS.POST,
  },
  getPaymentMethods: {
    url: "/api/payment-methods",
    method: HTTP_METHODS.GET,
  },
  getProducts: {
    url: "/api/products",
    method: HTTP_METHODS.GET,
  },
  getProductById: {
    url: "/api/products/:id",
    method: HTTP_METHODS.GET,
  },
  fileUpload: {
    url: "/api/file-upload",
    method: HTTP_METHODS.POST,
  },
  contact: {
    url: "/api/contact",
    method: HTTP_METHODS.POST,
  },

  // Plans & Addons
  postPlan: {
    url: "/api/plans",
    method: HTTP_METHODS.GET,
  },
  getAddonPlans: {
    url: "/api/my-account/get-addon",
    method: HTTP_METHODS.GET,
  },
  getAddon: {
    url: "/my-account/get-addon",
    method: HTTP_METHODS.GET,
  },
  createAddon: {
    url: "/api/create-addon",
    method: HTTP_METHODS.POST,
  },
  upgradePlan: {
    url: "/api/my-account/upgrade-plan",
    method: HTTP_METHODS.GET,
  },
  getUpgradedPlan: {
    url: "/api/upgrade-plan/available-plans",
    method: HTTP_METHODS.GET,
  },
  upgradePlanOrderSummary: {
    url: "/api/upgrade-plan/order-summary-after-billing-cycle",
    method: HTTP_METHODS.GET,
  },
  upgradePlanCheckout: {
    url: "/api/upgrade-plan/upgrade-after-billing-cycle",
    method: HTTP_METHODS.POST,
  },
  getNewLine: {
    url: "/api/get-newline",
    method: HTTP_METHODS.GET,
  },
  buyDataTopUp: {
    url: "/api/qr/buy-data-topup",
    method: HTTP_METHODS.POST,
  },
  topupOrderSummary: {
    url: "/api/topup-order-summary",
    method: HTTP_METHODS.GET,
  },

  // Billing & Payments
  verifyCustomer: {
    url: "/api/payment/verifyCustomer",
    method: HTTP_METHODS.POST,
  },
  orderSummary: {
    url: "/api/payment/payment-details",
    method: HTTP_METHODS.POST,
  },
  checkout: {
    url: "/api/payment/create-subscription",
    method: HTTP_METHODS.POST,
  },
  /** Poll Lago/async payment result after create-subscription returns payment_pending. */
  subscriptionStatus: {
    url: "/api/payment/subscription-status",
    method: HTTP_METHODS.GET,
  },
  paymentHistory: {
    url: "/api/my-account/lago-payment-history",
    method: HTTP_METHODS.GET,
  },
  generateBillPdf: {
    url: "/api/my-account/generate-bill-pdf",
    method: HTTP_METHODS.GET,
  },
  upcomingPlanInvoice: {
    url: "/api/my-account/generate-upcoming-plan-invoice",
    method: HTTP_METHODS.GET,
  },
  createCard: {
    url: "/api/card",
    method: HTTP_METHODS.POST,
  },
  getCards: {
    url: "/api/card/customer",
    method: HTTP_METHODS.GET,
  },
  deleteCard: {
    url: "/api/card",
    method: HTTP_METHODS.DELETE,
  },

  // Digital Security
  digitalSecurityPlans: {
    url: "/api/digital-security/plans",
    method: HTTP_METHODS.GET,
  },
  digitalSecurityDashboard: {
    url: "/api/digital-security/dashboard",
    method: HTTP_METHODS.GET,
  },
  digitalSecurityOrderSummary: {
    url: "/api/digital-security/order-summary",
    method: HTTP_METHODS.GET,
  },
  digitalSecurityCreatePayment: {
    url: "/api/digital-security/create-payment",
    method: HTTP_METHODS.POST,
  },
  digitalSecurityCurrentAndUpgradePlans: {
    url: "/api/digital-security/current-and-upgrade-plans",
    method: HTTP_METHODS.GET,
  },
  getSecurityPlansOrderSummary: {
    url: "/api/digital-security/upgrade-order-summary",
    method: HTTP_METHODS.GET,
  },
  digitalSecurityUpgradeOrderSummary: {
    url: "/api/digital-security/changeplan-order-summary",
    method: HTTP_METHODS.GET,
  },
  getUpgradeDigitalSecurityPlans: {
    url: "/api/digital-security/upgrade-plans",
    method: HTTP_METHODS.GET,
  },
  upgradeSecurityPlansCheckout: {
    url: "/api/digital-security/upgrade-downgrade",
    method: HTTP_METHODS.POST,
  },
  /** Mobile dashboard tab: upgrade security plan checkout (Digital Security tab uses upgrade-downgrade). */
  digitalSecurityMobileUpgradeCheckout: {
    url: "/api/digital-security/upgrade",
    method: HTTP_METHODS.POST,
  },

  digitalSecurityReactivationOrderSummary: {
    url: "/api/digital-security/reactivation-order-summary",
    method: HTTP_METHODS.GET,
  },
  digitalSecurityReactivate: {
    url: "/api/digital-security/reactivate",
    method: HTTP_METHODS.POST,
  },

  // Home Phone & Activation
  activateSim: {
    url: "/api/activate-sim",
    method: HTTP_METHODS.POST,
  },
  validateSim: {
    url: "/api/payment/validate-sim",
    method: HTTP_METHODS.POST,
  },
  mobileSimInventoryBySimNumber: {
    url: "/api/mobile-sim-inventory/simNumber",
    method: HTTP_METHODS.GET,
  },
  mobileSimInventoryPlans: {
    url: "/api/mobile-sim-inventory/plans",
    method: HTTP_METHODS.GET,
  },
  mobileSimInventorySecurityPlans: {
    url: "/api/mobile-sim-inventory/security-plans",
    method: HTTP_METHODS.GET,
  },
  mobileSimInventoryUpsellSummary: {
    url: "/api/mobile-sim-inventory/upsell-summary",
    method: HTTP_METHODS.POST,
  },
  mobileSimInventoryCreateSubscription: {
    url: "/api/mobile-sim-inventory/create-subscription-lago",
    method: HTTP_METHODS.POST,
  },
  checkSim: {
    url: "/api/payment/checkSim",
    method: HTTP_METHODS.POST,
  },
  validatePort: {
    url: "/api/validate-portin",
    method: HTTP_METHODS.POST,
  },
  transferNumber: {
    url: "/api/portin",
    method: HTTP_METHODS.POST,
  },
  checkPortStatus: {
    url: "/api/portin-check",
    method: HTTP_METHODS.POST,
  },
  updatePortin: {
    url: "/api/update-portin",
    method: HTTP_METHODS.POST,
  },
  sendQrOtp: {
    url: "/api/qr/send-otp",
    method: HTTP_METHODS.POST,
  },
  verifyQrOtp: {
    url: "/api/qr/verify-otp",
    method: HTTP_METHODS.POST,
  },
  viewQr: {
    url: "/api/view-qr",
    method: HTTP_METHODS.POST,
  },
  renewPlan: {
    url: "/api/qr/renew-plan",
    method: HTTP_METHODS.POST,
  },
  lockUnlockSim: {
    url: "/api/my-account/lock-unlock-sim",
    method: HTTP_METHODS.POST,
  },
  registerPSim: {
    url: "/api/my-account/register-psim",
    method: HTTP_METHODS.POST,
  },
  getFixedPhonePlans: {
    url: "/api/home-phone/plans",
    method: HTTP_METHODS.GET,
  },
  getFixedPhoneUpsellPlans: {
    url: "/api/home-phone/upsell-plans",
    method: HTTP_METHODS.GET,
  },
  fixedPhoneAccountDetails: {
    url: "/api/home-phone/account/details",
    method: HTTP_METHODS.GET,
  },
  fixedPhoneCreateSubscription: {
    url: "/api/home-phone/create-subscription-lago",
    method: HTTP_METHODS.POST,
  },
  fixedPhoneUpdateAddress: {
    url: "/api/home-phone/account/update-address",
    method: HTTP_METHODS.PUT,
  },
  fixedPhoneUpdateE911Address: {
    url: "/api/home-phone/account/update-e911-address",
    method: HTTP_METHODS.PUT,
  },
  fixedPhoneActivateLine: {
    url: "/api/home-phone/account/activate-line",
    method: HTTP_METHODS.POST,
  },
  fixedPhonePortin: {
    url: "/api/home-phone/account/portin",
    method: HTTP_METHODS.POST,
  },
  fixedPhonePortinCheck: {
    url: "/api/home-phone/account/portin-check",
    method: HTTP_METHODS.POST,
  },
  getFixedPhoneReviews: {
    url: "/api/home-phone/review/list",
    method: HTTP_METHODS.GET,
  },
  fixedPhoneDashboard: {
    url: "/api/home-phone/account/dashboard",
    method: HTTP_METHODS.GET,
  },
  fixedPhoneGenerateBillPdf: {
    url: "/api/home-phone/account/generate-bill-pdf",
    method: HTTP_METHODS.GET,
  },
  deviceDetails: {
    url: "/api/home-phone/device-details",
    method: HTTP_METHODS.GET,
  },
  fixedPhoneCheckoutExternalSummary: {
    url: "/api/home-phone/external-purchase/checkout/summary",
    method: HTTP_METHODS.POST,
  },
  fixedPhoneCheckoutExternalPrepaidSummary: {
    url: "/api/home-phone/external-purchase-prepaid/checkout/summary",
    method: HTTP_METHODS.POST,
  },
  fixedPhoneCreateSubscriptionExternal: {
    url: "/api/home-phone/create-subscription-external-lago",
    method: HTTP_METHODS.POST,
  },
  fixedPhoneCreateSubscriptionExternalPrepaid: {
    url: "/api/home-phone/external-purchase-prepaid/create-subscription-lago",
    method: HTTP_METHODS.POST,
  },
  fixedPhoneCheckoutSummary: {
    url: "/api/checkout/summary",
    method: HTTP_METHODS.POST,
  },
  freeProductVariant: {
    url: "/api/home-phone/free-product-variant",
    method: HTTP_METHODS.GET,
  },
  homePhoneMakePaymentOrderSummary: {
    url: "/api/home-phone/upcoming-bill",
    method: HTTP_METHODS.GET,
  },
  homePhoneMakePaymentCheckout: {
    url: "/api/home-phone/pay-advance",
    method: HTTP_METHODS.POST,
  },
  renewPlanMakePaymentOrderSummary: {
    url: "/api/subscription-extension/advance-payment-summary",
    method: HTTP_METHODS.GET,
  },
  renewPlanMakePaymentCheckout: {
    url: "/api/payment/process-manual-payment",
    method: HTTP_METHODS.POST,
  },
  reactivateOrderSummary: {
    url: "/api/home-phone/reactivate/summary",
    method: HTTP_METHODS.GET,
  },
  reactivateCheckout: {
    url: "/api/home-phone/reactivate",
    method: HTTP_METHODS.POST,
  },
  subscriptionExtensionReactivationSummary: {
    url: "/api/subscription-extension/reactivation-summary",
    method: HTTP_METHODS.GET,
  },
  subscriptionExtensionReactivate: {
    url: "/api/subscription-extension/reactivate",
    method: HTTP_METHODS.POST,
  },
  homePhoneUpdatePlanOrderSummary: {
    url: "/api/home-phone/update-plan/checkout/summary",
    method: HTTP_METHODS.POST,
  },
  homePhoneUpdatePlanCheckout: {
    url: "/api/home-phone/update-plan",
    method: HTTP_METHODS.POST,
  },
  getUpdatePlans: {
    url: "/api/home-phone/update/plans",
    method: HTTP_METHODS.GET,
  },
};
